"use client";

import { useState } from "react";
import { OptionCard } from "@/components/ui/option-card";
import type { Group } from "@/lib/types";

const PAGE_SIZE = 10;
const NEAREST_BADGE_MAX_KM = 50;

interface GroupSelectorProps {
  groups: Group[];
  onSelect: (group: Group) => void;
  title: string;
}

export function GroupSelector({ groups, onSelect, title }: GroupSelectorProps) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const nearest = groups.reduce<{ id: string; km: number } | null>(
    (min, g) =>
      g.distance_km != null && (min === null || g.distance_km < min.km)
        ? { id: g.id, km: g.distance_km }
        : min,
    null
  );
  const nearestId =
    nearest !== null && nearest.km <= NEAREST_BADGE_MAX_KM ? nearest.id : null;

  const visibleGroups = groups.slice(0, visibleCount);
  const remaining = groups.length - visibleCount;

  return (
    <div>
      <div className="relative sticky top-0 z-10 bg-background pt-2 pb-0">
        <h2 className="font-semibold text-2xl text-foreground leading-snug">
          {title}
        </h2>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 translate-y-full bg-gradient-to-b from-background to-transparent" />
      </div>
      <div className="mt-6 flex flex-col gap-2">
        {visibleGroups.map((group) => {
          const timeLabel = group.meeting_start_time
            ? group.meeting_start_time.slice(0, 5)
            : null;
          const sublabel =
            [group.meeting_day, timeLabel].filter(Boolean).join(" · ") ||
            undefined;
          return (
            <OptionCard
              badge={group.id === nearestId ? "Nearest" : undefined}
              key={group.id}
              label={group.name}
              onClick={() => onSelect(group)}
              sublabel={sublabel}
            />
          );
        })}
        {remaining > 0 && (
          <button
            className="mt-2 w-full rounded-lg py-3 text-muted-foreground text-sm transition-colors hover:text-foreground"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            type="button"
          >
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
